import { useCallback, useEffect, useMemo, useRef } from 'react';

import { getEnrichmentFailure, getEnrichmentSuggestion } from 'hooks/useGruAgent/helpers';
import { WorkflowGroup } from 'hooks/useGruAgent/types';
import { useGruAgent } from 'hooks/useGruAgent/useGruAgent';
import { retryWorkflow, triggerWorkflow } from 'services/gru/gruApi';
import { logError } from 'services/logging/logging';
import { MessageV2DTO, PartnerDTO, UserDTO } from 'support/types';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';

import { AgentsTabContentView } from './AgentsTabContentView';
import { AgentEnrichmentFailureContent } from './components/AgentEnrichmentFailureContent/AgentEnrichmentFailureContent';
import { AgentSuggestionContent } from './components/AgentSuggestionContent/AgentSuggestionContent';
import { ConnectionErrorFallback } from './components/ConnectionErrorFallback/ConnectionErrorFallback';

type AgentsTabContentProps = {
  message: MessageV2DTO;
  currentUser: UserDTO;
  currentPartner: PartnerDTO;
};

export const AgentsTabContent = ({ message, currentUser, currentPartner }: AgentsTabContentProps) => {
  const {
    workflowGroups,
    connected: agentIsConnected,
    connectionTimedOut,
    clear,
    agentName,
  } = useGruAgent({
    messageId: message.id,
    partnerId: currentPartner.id,
    partnerName: currentPartner.name,
    userName: currentUser.name,
    userLocale: currentUser.language,
    isAdmin: currentUser.isAdmin,
  });

  const triggerParams = useMemo(
    () => ({
      agentName,
      messageId: message.id,
      partnerId: currentPartner.id,
      partnerName: currentPartner.name,
      userName: currentUser.name,
      locale: currentUser.language,
      workflowType: 'enrichment' as const,
    }),
    [agentName, message.id, currentPartner.id, currentPartner.name, currentUser.name, currentUser.language],
  );

  const hasTriggered = useRef(false);
  useEffect(() => {
    if (agentIsConnected && workflowGroups.length === 0 && !hasTriggered.current) {
      hasTriggered.current = true;
      triggerWorkflow(triggerParams).catch(logError);
    }
  }, [agentIsConnected, workflowGroups.length, triggerParams]);

  const handleRetry = useCallback(async () => {
    const latestGroup = workflowGroups[workflowGroups.length - 1];
    if (!latestGroup) {
      return;
    }
    try {
      await retryWorkflow(agentName, latestGroup.workflow.id);
    } catch (e) {
      logError(e);
    }
  }, [workflowGroups, agentName]);

  const handleTrigger = useCallback(() => {
    hasTriggered.current = true;
    triggerWorkflow(triggerParams).catch(logError);
  }, [triggerParams]);

  useEffect(() => {
    if (currentUser.isAdmin) {
      (window as unknown as Record<string, unknown>).__clearAgentMessages = clear;
      (window as unknown as Record<string, unknown>).__triggerAgent = handleTrigger;
    }
    return () => {
      delete (window as unknown as Record<string, unknown>).__clearAgentMessages;
      delete (window as unknown as Record<string, unknown>).__triggerAgent;
    };
  }, [clear, handleTrigger, currentUser.isAdmin]);

  if (workflowGroups.length === 0 && connectionTimedOut && !agentIsConnected) {
    return <ConnectionErrorFallback />;
  }

  const historicalGroups = workflowGroups.slice(0, -1);
  const currentGroup = workflowGroups[workflowGroups.length - 1];

  return (
    <div className="px-0! *:not-first:mt-5 *:not-first:border-t *:not-first:pt-5 *:first:pt-0">
      {!historicalGroups?.length && !currentGroup ? (
        <div className="flex justify-center p-8">
          <LoadingSpinner />
        </div>
      ) : null}
      {historicalGroups.map((group) => (
        <HistoricalWorkflowCard key={group.workflow.id} group={group} message={message} />
      ))}
      {currentGroup && (
        <AgentsTabContentView
          workflow={currentGroup.workflow}
          actionResults={currentGroup.actionResults}
          message={message}
          onRetry={handleRetry}
        />
      )}
    </div>
  );
};

type HistoricalWorkflowCardProps = {
  group: WorkflowGroup;
  message: MessageV2DTO;
};

const HistoricalWorkflowCard = ({ group, message }: HistoricalWorkflowCardProps) => {
  const { actionResults, workflow } = group;

  const suggestion = getEnrichmentSuggestion(actionResults);
  if (suggestion) {
    if (workflow.status === 'applied') {
      return (
        <div className="flex flex-col gap-3 p-3">
          <AgentSuggestionContent
            actionResults={actionResults}
            agentProposal={suggestion}
            canonical={message.canonical}
            messageId={message.id}
          />
        </div>
      );
    }
    return null;
  }

  if (getEnrichmentFailure(actionResults)) {
    return <AgentEnrichmentFailureContent />;
  }

  return null;
};
